import React, { useEffect, useState } from 'react';

import { ReactComponent as DeleteIcon } from '../../../assets/icons/delete-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableConnectorPageFilterApplied,
  fetchAllConnectors,
  fetchConnectorPageFilters,
  refreshConnectors,
  setConnectorLimit,
  setConnectorPage,
  setConnectorSortBy,
  setConnectorSortField,
  updateConnectorPageFilters,
} from '../../../redux/slices/ConnectorSlice';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';
import AddConnectorRatingDrawer from '../../ev-device-hub/components/drawers/AddConnectorRatingDrawer';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}

/**
 * Local connector management component — displays connectors belonging to current tenant.
 * @returns {React.JSX.Element} Local connector component
 */
function Local() {
  const dispatch = useAppDispatch();
  const {
    connectors,
    loading,
    totalResults,
    totalPages,
    page,
    limit,
    filters,
    isFilterUpdated,
    filterListLoading,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.connector);

  const [ratingDrawerOpen, setRatingDrawerOpen] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: null,
    header: false,
    content: null,
    notificationModal: false,
  });

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllConnectors({
        page: DefaultApiLimit.page,
        limit,
        sortBy,
        sortField,
        ...queryParameters,
      }),
    );
  }, []);

  useEffect(() => {
    if (isFilterUpdated) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      dispatch(
        fetchAllConnectors({
          page: DefaultApiLimit.page,
          limit,
          sortBy,
          sortField,
          ...queryParameters,
        }),
      );
      dispatch(disableConnectorPageFilterApplied());
    }
  }, [isFilterUpdated]);

  /**
   * Handles sort change from CustomTable
   * @param {any} sortData - Sort data payload
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setConnectorSortBy(sortData.sortBy));
    dispatch(setConnectorSortField(sortData.sortField));
    dispatch(
      fetchAllConnectors({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField: sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      }),
    );
  };

  /**
   * Handles pagination and limit changes
   * @param {any} value - Page and limit data
   * @param {any} value.page - The page prop
   * @param {any} value.limit - The limit prop
   */
  const handlePageLimitChange = (value: { page?: number; limit?: number }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (value.page !== undefined) dispatch(setConnectorPage(value.page));
    if (value.limit !== undefined) dispatch(setConnectorLimit(value.limit));
    dispatch(
      fetchAllConnectors({
        ...queryParameters,
        ...value,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes connector data using current filters
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      fetchAllConnectors({
        ...queryParameters,
        page,
        limit,
        sortField,
        sortBy,
      }),
    );
  };

  /**
   * Refreshes connector data using current filters and toggle refresh
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      refreshConnectors({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  /**
   * Handles confirm delete rating.
   * @param {string} ratingId - The ID of the rating to delete.
   */
  const handleConfirmDeleteRating = (ratingId: string) => {
    deviceApi
      .deleteConnectorRating(ratingId)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title="Connector rating deleted successfully"
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                handleRefresh();
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        setModalBoxStatus('close');
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: error?.response?.data?.message || 'Something went wrong',
            code: error?.response?.data?.code || error?.response?.status || 500,
          }),
        );
      });
  };

  /**
   * Handles delete rating click.
   * @param {string} ratingId - The ID of the rating to delete.
   */
  const handleDeleteRatingClick = (ratingId: string) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={ratingId}
          title="Are you sure you want to delete this connector rating?"
          description="This action cannot be undone."
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleConfirmDeleteRating(id)}
        />
      ),
      notificationModal: true,
    });
  };

  const column = [
    {
      label: 'Name',
      accessorKey: 'name',
      sortAllow: true,
      /**
       * Renders name
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.name || '-'}</span>,
    },
    {
      label: 'Current Type',
      accessorKey: 'currentType',
      /**
       * Renders connector type
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span className="current-type-label">{currentRow.currentType?.toUpperCase() || '-'}</span>
      ),
    },
    {
      label: 'Max Power Output (Kw)',
      accessorKey: 'capacityKW',
      sortAllow: true,
      /**
       * Renders max power output
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <span>{currentRow.capacityKW ? `${currentRow.capacityKW}` : '-'}</span>
      ),
    },
    {
      label: 'Device Count',
      accessorKey: 'deviceCount',
      sortAllow: true,
      /**
       * Renders device count
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => <span>{currentRow.deviceCount || 0}</span>,
    },
    {
      label: 'Action',
      accessorKey: 'action',
      sortAllow: false,
      columnWidth: 80,
      sticky: 'right' as const,
      /**
       * Renders action
       * @param {any} currentRow - row data
       * @returns {any} JSX element
       */
      customData: (currentRow: any) => (
        <div className="action-buttons-wrap">
          <div
            role="presentation"
            className="action-button"
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteRatingClick(currentRow._id);
            }}>
            <DeleteIcon className="icon delete" />
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        header={modalBoxDetail.header}
        content={modalBoxDetail.content}
        notification={modalBoxDetail.notificationModal}
        modalBoxAnimation="modalAnimateToLeft"
        closeModal={() => setModalBoxStatus('close')}
      />
      <CustomTable
        dataKey="ConnectorManagement"
        header={column}
        data={connectors}
        serialNo
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        height={350}
        created
        lastUpdated
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={handlePageLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateConnectorPageFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConnectorPageFilters}
        actionButton
        refreshRequired
        handleRefresh={handleRefresh}
        hoverLabel="Refresh"
        refershCurrentPages={handleRefresh}
        onHiddenColumnsChange={() => {}}
        rightTopContent={
          <div className="table-bottom-button-list">
            {permissionCheck('devicesRatingAdd') && (
              <div className="table-button-item">
                <MyButton
                  label="Add Connector Rating"
                  buttonType="submit"
                  onClickFunc={() => setRatingDrawerOpen(true)}
                />
              </div>
            )}
          </div>
        }
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
      />

      <AddConnectorRatingDrawer
        open={ratingDrawerOpen}
        onClose={() => setRatingDrawerOpen(false)}
        existingValues={connectors.map((c: any) => c.name || c._id)}
        onRatingCreated={() => {
          setRatingDrawerOpen(false);
          handleRefresh();
        }}
      />
    </>
  );
}

export default Local;
